/** Turns an API line item into the workbook's row shape. Used by the estimate ETL. */
import { LineItem, LineItemData } from "../../interfaces/LineItem";
import { Alternate } from "../../interfaces/Alternate";
import { EstimateUseGroup } from "../../interfaces/EstimateUseGroup";
import { EstimateSystem } from "../../interfaces/EstimateData";
import { FloorGroup } from "../../interfaces/AreaSheet";
import { defaultLineItem } from "../utils/lineItems";

export function parseLineItem(
  data: LineItemData,
  alternates: Alternate[],
  useGroups: { [key: string]: EstimateUseGroup },
  systems: EstimateSystem[],
  floorGroups: FloorGroup[]
): LineItem {
  const lineItem: LineItem = {
    ...defaultLineItem,
    ...data,
    notes: data.notes && data.notes.startsWith("=") ? `"${data.notes}"` : data.notes,
  };
  const calculatedLineItem = calculateUnitCosts(lineItem);
  const alternate = alternates.find((alt) => alt.id === data.alternate) || null;
  const alternate_desc = alternate ? alternate.label || "" : "";
  const alternate_code = alternate ? alternate.key || "" : "";
  const alternateId = alternate ? alternate.id || "" : "";
  const estimateSystem = systems.find((system) => system.id === data.estimate_system)?.system_name || "";

  function useGroupsLabel(): string {
    if (!data.estimate_use_groups || !useGroups) return "";

    const labels = Object.keys(data.estimate_use_groups)
      .map((key) => useGroups[key])
      .filter((use): use is EstimateUseGroup => use !== undefined)
      .sort((a, b) => a.order - b.order)
      .map((use) => (use.label && use.label !== "" ? use.label : use.use_group));

    return labels.join(", ");
  }

  function floorGroupsLabel(): string {
    if (!data.estimate_use_groups || !useGroups) return "";

    const labels = Object.keys(data.estimate_use_groups)
      .map((key) => useGroups[key])
      .filter((use): use is EstimateUseGroup => use !== undefined)
      .map((use) => {
        const fg = floorGroups.find((fg) => fg.id === use.floor_group);
        return fg ? (fg.label && fg.label !== "" ? fg.label : fg.label) : "";
      });

    const uniqueLabels = Array.from(new Set(labels)).sort((a, b) => a.localeCompare(b));
    return uniqueLabels.join(", ");
  }

  return {
    ...calculatedLineItem,
    use_group: useGroupsLabel(),
    floor_group: floorGroupsLabel(),
    quantity_take_off: data.quantity_formula?.parts?.[0]?.quantity_take_off || "",
    estimate_system: estimateSystem,
    alternateId,
    alternate_desc,
    alternate_code,
    alternate: alternate_code ? alternate_code + " - " + alternate_desc : "",
    alternateStatus: alternate ? alternate.status || "" : "",
    baseBid: alternate ? alternate.accepted : true,
  } as LineItem;
}

function calculateUnitCosts(lineItem: LineItem): LineItem {
  if (lineItem.total_cost_text) {
    return {
      ...lineItem,
      total: 0,
      total_uc: 0,
      labor_uc: 0,
      labor_rate: 0,
      prod_rate: 0,
      material_uc: 0,
      sub_uc: 0,
      equip_uc: 0,
      other_uc: 0,
      lmseo_prorated: false,
      labor_rate_prorated: false,
    };
  }

  const total =
    lineItem.gcgr_line_item != null ? lineItem.total_cost : (lineItem.quantity || 0) * (lineItem.total_uc || 0);
  let { labor_uc, labor_rate, prod_rate, material_uc, sub_uc, equip_uc, other_uc, total_uc } = lineItem;
  labor_uc = labor_uc ?? 0;
  labor_rate = labor_rate ?? 0;
  prod_rate = prod_rate ?? 0;
  material_uc = material_uc ?? 0;
  sub_uc = sub_uc ?? 0;
  equip_uc = equip_uc ?? 0;
  other_uc = other_uc ?? 0;
  total_uc = total_uc ?? 0;

  const tolerance = 0.001;

  const totalUnitCostCalculated = labor_uc + material_uc + sub_uc + other_uc + equip_uc;
  const totalOverridden = Math.abs(total_uc - totalUnitCostCalculated) > tolerance;
  if (totalOverridden) {
    if (totalUnitCostCalculated === 0) {
      sub_uc = total_uc; //if lmseo is all zero, set the sub_uc to total_uc
    } else {
      labor_uc = total_uc * (labor_uc / totalUnitCostCalculated || 0);
      material_uc = total_uc * (material_uc / totalUnitCostCalculated || 0);
      sub_uc = total_uc * (sub_uc / totalUnitCostCalculated || 0);
      equip_uc = total_uc * (equip_uc / totalUnitCostCalculated || 0);
      other_uc = total_uc * (other_uc / totalUnitCostCalculated || 0);
    }
  }

  const laborCalculated = labor_rate * prod_rate;
  const laborOverridden = Math.abs(labor_uc - laborCalculated) > tolerance;

  // Preserve the user-entered prod_rate before synthesis overwrites it.
  // Only differs from prod_rate when labor_uc is overridden and prod_rate was 0
  // (in which case prod_rate gets set to 1 below).
  const prod_rate_original = prod_rate;

  if (laborOverridden) {
    if (prod_rate === 0) {
      labor_rate = labor_uc;
      prod_rate = 1;
    } else {
      labor_rate = labor_uc / prod_rate;
    }
  }

  return {
    ...lineItem,
    total,
    labor_uc,
    labor_rate,
    prod_rate,
    prod_rate_original,
    material_uc,
    sub_uc,
    equip_uc,
    other_uc,
    lmseo_prorated: totalOverridden,
    labor_rate_prorated: laborOverridden,
  };
}
